import { apsEnums } from "../../shared/dto/aps.enums";
import { apsViewModels } from "../../shared/dto/APS.ViewModels";
import * as angular from 'angular';
import * as moment from 'moment';
import * as _ from 'lodash';
import { apsDisplayEnums } from "../../shared/dto/enums";
import { Plant } from "../navigation";
import { apsCommon } from "../../shared/dto/aps.common";
import EquipmentServiceName, { EquipmentService } from "../../services/equipment/equipment.service";
import PlantServiceName, { PlantService } from '../../services/plant/plant.service';
import LoaderServiceName, { LoaderService } from "../../components/loader/loader.service";
import { StateParams } from "../../common";
import { eq, keyBy } from "lodash";

let requires = [EquipmentServiceName, PlantServiceName, LoaderServiceName];

export interface LineCountsService {
  createLineCounts(
    lines: apsViewModels.ShopInspectionLines,
    counts: { [key: string]: apsViewModels.CountItem[] },
    plant: Plant,
    countsInterval: apsDisplayEnums.CountDisplayInterval,
    canDisable: boolean,
    shopConfig: apsViewModels.ShopConfigurationWithShifts,
    isCountsV2?: boolean,
    calcGroupByShears?:boolean): LineCounts;
  createLineEquipmentCounts(
    tabs: apsViewModels.LineEquipmentTab[],
    lineCounts: LineCounts,
    details: apsViewModels.LineEquipmentDataPointItem[],
    counts: { [key: string]: apsViewModels.CountItem[] },
    countsInterval: apsDisplayEnums.CountDisplayInterval,
    equipment: LineEquipmentItem,
    group: LineEquipmentGroup): LineEquipmentCounts;
}

export interface LineCounts extends apsViewModels.ShopInspectionLines {
  lines: LineItem[];
  junctions: LineJunctionItem[];

  equipmentGroups: LineEquipmentGroup[];
  equipmentGroupsV1: LineEquipmentGroup[];
  equipment: { [id: string]: LineEquipmentItem };

  kpis: KeyPerformanceIndicator[];
  kpisV1: KeyPerformanceIndicator[];
  gobsProduced: Counts;
  packedCount: Counts;
  countRejectPercent: apsEnums.CountRejectPercent;
  shopConfig: apsViewModels.ShopConfigurationWithShifts;
  heInId: string;
  lehrInId: string;
  packedCountId: string;
  firstInspectionId: string;
  isV2?: boolean;
  isCalcGroupByShears?: boolean;
  usePercentGobs: boolean;

  handleInterval(event: apsViewModels.ShopCountIntervalBatchAdded): boolean;
  handleCount(event: apsViewModels.ShopCountBatchAdded): boolean;
  handleEquipmentHealthUpdate(event: apsViewModels.EquipmentHealthUpdated): void;
  updateCounts(
    counts: { [key: string]: apsViewModels.CountItem[] },
    shopConfig: apsViewModels.ShopConfigurationWithShifts
  ): boolean;
  handleIntervalChange(changedInterval: apsDisplayEnums.CountDisplayInterval): void;
  handleInconsistencyChanged(event: apsViewModels.InconsistentEquipmentConfigurationChanged): void;

  currentDetailCounts?: LineEquipmentCounts;
  currentGroup?: LineEquipmentGroup;
  kpiCalculations: apsViewModels.KpiCalculation[];
}

export interface LineEquipmentDataPoint {
  name: string;
  count: Count;
  order: number;
}

export interface LineEquipmentCounts {
  tabs: LineEquipmentTab[];
  details: LineEquipmentDataPoint[];
  counts: { [id: string]: Counts[] };
  updateCounts(
    counts: { [key: string]: apsViewModels.CountItem[] },
    shopConfig: apsViewModels.ShopConfigurationWithShifts): boolean;
  handleInterval(
    event: apsViewModels.ShopCountIntervalBucket,
    counts: apsViewModels.ShopCountIntervalAdded[]): boolean;
  handleCount(
    event: apsViewModels.ShopCountBatchAdded,
    counts: apsViewModels.ShopCountAdded[]): boolean;
  handleIntervalChange(changedInterval: apsDisplayEnums.CountDisplayInterval): void;
  onUpdated: (() => void)[];

  equipment?: LineEquipmentItem;
  group?: LineEquipmentGroup;
}

export interface LineItem extends apsViewModels.LineItem {
  equipment: LineEquipmentItem[];
  startJunction: LineJunctionItem;
  endJunction: LineJunctionItem;
}

export interface LineJunctionItem extends apsViewModels.LineJunctionItem {
  inputs: LineItem[];
  outputs: LineItem[];
}

export interface LineEquipmentDetail {
  name: string;
  count: number;
  actualCount: Count;
  percent: string | undefined;
  getPercent(): string;
  showTrend?: boolean;
  trend?: Trend;
  trendCounts?: number[];
}

export class LineEquipmentDetailImpl implements LineEquipmentDetail {
  name: string;
  get count(): number {
    return this.actualCount.count;
  }
  actualCount: Count;
  get percent(): string {
    return this.actualCount.percent;
  }
  getPercent(): string {
    return this.actualCount.getPercent();
  }
  get showTrend() {
    return this.actualCount.showTrend;
  }
  get trend() {
    return this.actualCount.trend;
  }
  get trendCounts() {
    return this.actualCount.trendCounts;
  }

  constructor(name: string, count: Count) {
    this.name = name;
    this.actualCount = count;
  }
}

export interface LineEquipmentItem extends apsViewModels.LineEquipmentItem {
  counts: { [id: string]: Counts[] };
  inspectCount: Counts;
  rejectCount: Counts;
  lineHealthCssBox: string;
  healthCssBox: string;
  healthCssText: string;
  line: LineItem;
  lineEquipmentDetail: LineEquipmentDetail[];
  canMoveLeft: boolean;
  canMoveRight: boolean;
  inspectCountHidden: boolean;
  inspectPercentVisible: boolean;
  rejectPercentHidden: boolean;
  rejectMinPercentage: number;
  rejectMaxPercentage: number;

  handleInterval(
    event: apsViewModels.ShopCountIntervalBucket,
    counts: apsViewModels.ShopCountIntervalAdded[]): boolean;
  handleCount(
    event: apsViewModels.ShopCountBatchAdded,
    counts: apsViewModels.ShopCountAdded[]): boolean;
  handleEquipmentHealthUpdate(event: apsViewModels.EquipmentHealthUpdated): void;
  updateCounts(
    counts: { [key: string]: apsViewModels.CountItem[] },
    shopConfig: apsViewModels.ShopConfigurationWithShifts): boolean;
  getHealthCssClass(elementType: string): string;
  handleIntervalChange(changedInterval: apsDisplayEnums.CountDisplayInterval): void;
}

export interface LineEquipmentGroup {
  lineEquipmentNameIds: number[];
  types: apsEnums.LineEquipmentType[];
  names: string[];
  name: string;
  kpiName?: string;
  equipment: { [id: string]: LineEquipmentItem };

  inspectCount: Count;
  hasRejects: boolean;
  rejectCount?: Count;
  wareLossPercent?: string;
  efficiencyAgainstShears?: string;
  lineStartJunctionId?: string;
  order?: number;
  lineLineEquipmentTabs: { [key: string]: apsViewModels.LineEquipmentTab[] };
  rejects?: apsViewModels.EquipmentRejects[];
  groupUpdated?(): void;
}

export interface Trend {
  up?: boolean;
  flat?: boolean;
  down?: boolean;
}

export interface Count {
  count: number;
  percent?: string;
  gobPercent?: string;
  trend?: Trend;
  inspectCount?: Count;
  showPercent?: boolean;
  showTrend?: boolean;
  rows?: LineEquipmentRow[];
  rejectCounts?: Count[];
  aggregateCounts?: AggregateCount[];
  cells?: LineEquipmentCell[];
  trendCounts?: number[];
  setTrendCounts?: () => void;
  parent?: Count;
  rejectPercentDecimals: number;
  lineCounts: LineCounts;
  getPercent(): string;
}

export interface AggregateCount extends Count {
  memberCounts: Counts[];
  finalCounts: Count[];
  countsMissing: boolean;
  mold?: number;
  isMnr?: boolean;
  calculateCount: () => void;
}

export interface Counts extends Count {
  id: string;
  counts: CountItem[];
  filteredCounts: CountItem[];
  hasMolds: boolean;
  speed?: string;
  showSpeed?: boolean;
  moldCounts?: { [key: number]: Count };
  mnrCount?: Count;
  moldMnrCounts?: { [key: number]: Count };

  countsInterval: apsDisplayEnums.CountDisplayInterval;

  showPerformance: boolean;

  handleInterval?: (
    event: apsViewModels.ShopCountIntervalBucket,
    counts: apsViewModels.ShopCountIntervalAdded) => boolean;
  handleCount?: (
    event: apsViewModels.ShopCountBatchAdded,
    count: apsViewModels.ShopCountAdded) => boolean;
  updateCounts?: (
    counts: apsViewModels.CountItem[],
    shopConfig: apsViewModels.ShopConfigurationWithShifts) => boolean;
  calculateCount?: () => boolean;

  countsUpdated: { [key: string]: boolean };
  shopConfig: apsViewModels.ShopConfigurationWithShifts;
}

export interface LineEquipmentTab extends apsViewModels.LineEquipmentTab {
  equipment: LineEquipmentItem;
  rows: LineEquipmentRow[];
  expandedChildren: LineEquipmentRow[];
  needsRowUpdate: boolean;
  outputRows: LineEquipmentRow[];
  updateRows: () => void;
  counts: LineEquipmentCounts;
}

export interface LineEquipmentCell extends apsViewModels.LineEquipmentCell {
  row: LineEquipmentRow;
  count: Count;
  affectsRowDisplay: boolean;
}

export interface RowParent {
  expandedChildren: LineEquipmentRow[];
  parent?: RowParent;
  needsRowUpdate?: boolean;
  cells?: LineEquipmentCell[];
  isMold?: boolean;
  mold?: number;
  id?: string;
  hasMNR?: boolean;
}

export interface LineEquipmentRow extends apsViewModels.LineEquipmentRow {
  children: LineEquipmentRow[];
  isNoCount: boolean;
  count: RowCount;
  parent: RowParent;
  expandedChildren: LineEquipmentRow[];
  cells: LineEquipmentCell[];
  displayName: string;
  cellCounts: CellCounts[];
  outputRows: LineEquipmentRow[];
  id?: string;
  sortOrder?: number;
  setRowsUpdateNeeded: () => void;
  isChild: boolean;
}

export interface RowCount extends Count {
  moldCounts?: { [key: number]: Count };
  memberCounts?: RowCount[];
}

export interface CellCounts extends Array<Counts> {
  moldCounts?: { [key: number]: Count };
}

export interface CountItem {
  intervalStartTime: number;
  intervalEndTime: number;
  lastEndTime?: number;
  interval: apsEnums.CountInterval;
  value: number;
  moldCounts: {
    [key: number]: {
      value: number;
      mnrCount?: number;
    }
  };
  mnrCount?: number;
}

export interface KeyPerformanceIndicator {
  numerator: Counts;
  denominator: Counts;
  efficiency: string;
  lossPercent: string;
  losses: number;
  efficiencyName: string;
  efficiencyFormula: string;
  lossesName: string;
  recalculate(): void;
  name?: string
}

// mold count for getByDatapointID endpoint on Counts
export interface IMoldCount {
  cavity: string;
  isMachineId: string;
  section: number;
  gob: number;
  mold: number;
  count: number;
  mnrCount: number;
}

// count for getByDatapointID endpoint on Counts
export interface ICountByDataPointId {
  intervalStart: Date;
  intervalEnd: Date;
  interval: number;
  dataPointId: string;
  defectTypeId: string;
  value: number;
  moldCounts: IMoldCount[];
}

export default angular
  .module('counts', requires)
  .service('lineCountsService', (equipmentService: EquipmentService, 
                plantService: PlantService, loaderService: LoaderService, $stateParams: StateParams): LineCountsService => {

    let kpiPairs = [
      {
        numerator: apsEnums.WellKnownCheckType.LehrInput,
        denominator: apsEnums.WellKnownCheckType.GobsProduced,
        efficiency: 'H/E Efficiency',
        losses: 'H/E Losses'
      },
      {
        numerator: apsEnums.WellKnownCheckType.PackedCount,
        denominator: apsEnums.WellKnownCheckType.LehrInput,
        efficiency: 'C/E Efficiency',
        losses: 'C/E Losses'
      },
      {
        numerator: apsEnums.WellKnownCheckType.PackedCount,
        denominator: apsEnums.WellKnownCheckType.GobsProduced,
        efficiency: 'Pack Efficiency',
        losses: 'Overall Losses'
      }
    ];

    
    let kpiPairsV1 = [
      {
        numerator: apsEnums.WellKnownCheckType.LehrInput,
        denominator: apsEnums.WellKnownCheckType.GobsProduced,
        efficiencyText: 'LEHR IN',
        efficiencyFormula: '100 * (LEHR IN / SHEARS)',
        lossText: 'HOT END LOSS',
        lossFormula: '100 - (100 * (LEHR IN / SHEARS))',
        name: 'HE',
        order: 1
      },
      {
        numerator: apsEnums.WellKnownCheckType.LehrOutput,
        denominator: apsEnums.WellKnownCheckType.GobsProduced,
        efficiencyText: 'LEHR OUT',
        efficiencyFormula: '100 * (LEHR OUT / SHEARS)',
        lossText: 'LEHR LOSS',
        lossFormula: '100 - (100 * ((LEHR IN - LEHR OUT) / SHEARS))',
        name: 'LEHR',
        order: 2
      },
      {
        numerator: apsEnums.WellKnownCheckType.ColdEndInspectionLoss,
        denominator: apsEnums.WellKnownCheckType.GobsProduced,
        efficiencyText: null,
        efficiencyFormula: null,
        lossText: 'AIE LOSS',
        lossFormula: '100 - (100 * (AIE LOSS / SHEARS))',
        name: 'AIE',
        order: 3
      },
      {
        numerator: apsEnums.WellKnownCheckType.ColdEndTotalLoss,
        denominator: apsEnums.WellKnownCheckType.GobsProduced,
        efficiencyText: null,
        efficiencyFormula: null,
        lossText: 'CE DELTA LOSS',
        lossFormula: '100 - (100 * (LEHR OUT - AIE LOSS - END OF LINE) / SHEARS)',
        name: 'CE DELTA',
        order: 4
      },
      {
        numerator: apsEnums.WellKnownCheckType.PackedCount,
        denominator: apsEnums.WellKnownCheckType.GobsProduced,
        efficiencyText: 'PACK',
        efficiencyFormula: '100 * (END OF LINE / SHEARS)',
        lossText: 'TOTAL LOSS',
        lossFormula: '100 - (100 * (END OF LINE / SHEARS))',
        name: 'PACK',
        order: 5
      }
    ];



    function calcEfficiency(numerator: Counts, denominator: Counts): string {
      return numerator && denominator && denominator.count > 0
        ? ((numerator.count / denominator.count) * 100).toFixed(1) + '%'
        : 'N/A';
    }

    function calcLosses(numerator: Counts, denominator: Counts): number {
      return numerator && denominator && denominator.count >= numerator.count
        ? denominator.count - numerator.count
        : 0;
    }

    function calcLossPercent(losses: number, denominator: Counts): string {
      return denominator && denominator.count > 0
        ? ((losses / denominator.count) * 100).toFixed(1) + '%'
        : 'N/A';
    }

    function calcSpeed(count: Counts): string {
      if (!count) {
        return '';
      }
      var batchCounts = count.filteredCounts.filter(x => !x.lastEndTime);
      let speedCount = batchCounts.map(x => x.value).reduce((p,c) => p+c, 0);
      if (speedCount === 0) {
        return '';
      }

      let minValue = _.min(_.map(batchCounts, 'intervalStartTime'));
      let maxValue = _.max(_.map(batchCounts, 'intervalEndTime'));
      let interval = moment(maxValue).diff(moment(minValue), 'minutes', true);
      return Math.round(speedCount / interval) + ' BPM';
    }

    function createCurrentBucket(): CountItem {
      return {
        value: 0,
        interval: apsEnums.CountInterval.Minute,
        intervalStartTime: moment().startOf('m').toDate().getTime(),
        intervalEndTime: moment().add(1, 'm').startOf('m').toDate().getTime(),
        moldCounts: {}
      };
    }

    function isPlantCheck($stateParams: any) {
      return ($stateParams.plantId === 'bbdf0000-3a00-000d-9cf9-08d2e5fa8dba' ||
        $stateParams.plantId === 'bbdf0000-3a00-000f-724d-08d2e5fa8e34');
    }

    let rejectDecimalOveride = 1;
    if (isPlantCheck($stateParams)) {
      rejectDecimalOveride = 2;
    }
    let defaultCount: Count = {
      count: 0,
      percent: '0.0',
      gobPercent: '0.0',
      trend: { flat: true },
      rejectPercentDecimals: rejectDecimalOveride,
      lineCounts: null,
      getPercent: () => '0.0'
      // rejectPercentDecimals: 2
    };

    function mapMoldCount(
      moldCounts: { [key: number]: { value: number, mnrCount?: number } },
      mc: apsCommon.MoldCount): void {
      if (!mc.count) {
        return;
      }

      let moldCount = moldCounts[mc.mold];
      if (moldCount) {
        moldCount.value += mc.count;
      } else {
        moldCount = { value: mc.count };
        moldCounts[mc.mold] = moldCount;
      }

      if (mc.mnrCount) {
        if (moldCount.mnrCount) {
          moldCount.mnrCount += mc.mnrCount;
        } else {
          moldCount.mnrCount = mc.mnrCount;
        }
      }
    }

    function mapMoldCounts(moldCounts: apsCommon.MoldCount[]): { [key: number]: { value: number, mnrCount?: number } } {
      let mappedCounts: { [key: number]: { value: number, mnrCount?: number } } = {};
      moldCounts.forEach(mc => mapMoldCount(mappedCounts, mc));
      return mappedCounts;
    }

    function getTotalMnrCount(moldCounts: apsCommon.MoldCount[]): number {
      let mnrCount = null;
      moldCounts.forEach(mc => {
        if (mc.mnrCount) {
          if (mnrCount) {
            mnrCount += mc.mnrCount;
          } else {
            mnrCount = mc.mnrCount;
          }
        }
      });
      return mnrCount;
    }

    function mapCount(count: {
      intervalStart: Date;
      intervalEnd: Date;
      interval: apsEnums.CountInterval;
      value: number;
      moldCounts: apsCommon.MoldCount[];
    }): CountItem {
      return {
        value: count.value,
        interval: count.interval,
        intervalStartTime: count.intervalStart.getTime(),
        intervalEndTime: count.intervalEnd.getTime(),
        moldCounts: count.moldCounts
          ? mapMoldCounts(count.moldCounts)
          : {},
        mnrCount: count.moldCounts
          ? getTotalMnrCount(count.moldCounts)
          : undefined
      };
    }

    function calcPercent(numerator: number, denominator: number): number {
      if (numerator <= 0 || denominator <= 0) {
        return 0;
      }
      return Math.round((numerator * 10000.0) / denominator) / 100.0;
    }

    function calcPercentStr(numerator: number, denominator: number): string {
      if (numerator == 0 || denominator == 0) {
        return "0";
      }
      return numerator && denominator > 0
      ? ((numerator / denominator) * 100).toFixed(1) 
      : 'N/A';

    }

    function calcPercentStrTwoDecimals(numerator: number, denominator: number): string {
      if (numerator == 0 || denominator == 0) {
        return "0";
      }
      return numerator && denominator > 0
      ? ((numerator / denominator) * 100).toFixed(2) 
      : 'N/A';

    }

    function calcPercentFromOneStr(numerator: number, denominator: number): string {
      if (numerator <= 0 || denominator <= 0) {
        return "0";
      }
      return numerator && denominator > 0
      ? ((1- (numerator / denominator)) * 100).toFixed(1) 
      : 'N/A';

    }

    function calcCountRejectPercent(count: Count): string {
      return calcRejectPercent(count, count.inspectCount);
    }

    function calcCountRejectGobPercent(count: Count): string {
      return calcRejectPercent(count, !count.lineCounts ? null : count.lineCounts.gobsProduced);
    }

    function calcRejectPercent(count: Count, denominatorCount: Count): string {
      if (!count || !denominatorCount || count.count <= 0 || denominatorCount.count <= 0) {
        return defaultCount.percent;
      }
      // return calcCountPercent(count).toFixed(count.rejectPercentDecimals);

      if (isPlantCheck($stateParams)) {
        return calcPercent(count.count, denominatorCount.count).toFixed(2);
      } else {
        return calcPercent(count.count, denominatorCount.count).toFixed(1);
      }
    }

    function trendCountsFilter(count: CountItem): boolean {
      return count.interval === apsEnums.CountInterval.TenMinutes
        || count.interval === apsEnums.CountInterval.Hour;
    }

    function getTrendCounts(counts: Counts): CountItem[] {
      return _.take(_.orderBy(counts.counts.filter(trendCountsFilter),
        ['intervalEndTime'], ['desc']), 4);
    }

    function calcTrend(inspectCounts: number[], rejectCounts: number[]): Trend {
      if (!inspectCounts || !rejectCounts || inspectCounts.length !== rejectCounts.length) {
        return defaultCount.trend;
      }

      let direction = 0;
      let previous = null;
      let current = null;
      for (let i = 0; i < inspectCounts.length; i++) {
        current = calcPercent(rejectCounts[i], inspectCounts[i]);
        if (previous !== null) {
          if (current > previous) {
            direction--;
          } else if (current < previous) {
            direction++;
          }
        }
        previous = current;
      }

      if (direction === (inspectCounts.length - 1)) {
        return { up: true };
      }
      if (direction === (1 - inspectCounts.length)) {
        return { down: true };
      }

      return defaultCount.trend;
    }

    function setCountTrendCounts(count: Count): void {
      let parent = count;
      do {
        if (parent.setTrendCounts) {
          parent.setTrendCounts();
          break;
        }
        parent = parent.parent;
      } while (parent);
    }

    function calcCountRejectTrend(count: Count): Trend {
      if (!count || !count.inspectCount || !count.trendCounts) {
        return defaultCount.trend;
      }
      if (!count.inspectCount.trendCounts) {
        setCountTrendCounts(count.inspectCount);
      }
      if (!count.inspectCount.trendCounts) {
        return defaultCount.trend;
      }
      return calcTrend(count.inspectCount.trendCounts, count.trendCounts);
    }

    function mnrTrendMap(i: CountItem) { return i.mnrCount || 0; }

    function setTrendCounts(counts: Counts): void {
      let trendCounts = getTrendCounts(counts);
      counts.trendCounts = trendCounts.map(i => i.value);
      if (!counts.hasMolds) {
        return;
      }

      for (let mold in counts.moldCounts) {
        let moldCount = counts.moldCounts[mold];
        let moldMap = (i: CountItem) => {
          let count = i.moldCounts[mold];
          return count ? count.value : 0;
        };
        moldCount.trendCounts = trendCounts.map(moldMap);
      }

      if (counts.mnrCount) {
        counts.mnrCount.trendCounts = trendCounts.map(mnrTrendMap);
      }

      for (let mold in counts.moldMnrCounts) {
        let mnrCount = counts.moldMnrCounts[mold];
        let moldMnrMap = (i: CountItem) => {
          let count = i.moldCounts[mold];
          return count ? (count.mnrCount || 0) : 0;
        };
        mnrCount.trendCounts = trendCounts.map(moldMnrMap);
      }
    }

    function recalculatePercentTrend(count: Count): void {
      if (!count.inspectCount) {
        return;
      }

      if (count.showPercent) {
        count.percent = calcCountRejectPercent(count);
        count.gobPercent = calcCountRejectGobPercent(count);
      }

      if (count.showTrend) {
        count.trend = calcCountRejectTrend(count);
      }
    }

    function createSubCount(model: Counts, count: number): Count {
      let rejectDecimalOveride = 1;
      if (isPlantCheck($stateParams)) {
        rejectDecimalOveride = 2;
      }
      var countModel = {
        count: count,
        inspectCount: model.inspectCount,
        showPercent: model.showPercent,
        showTrend: model.showTrend,
        percent: defaultCount.percent,
        gobPercent: defaultCount.gobPercent,
        trend: defaultCount.trend,
        parent: model,
        rejectPercentDecimals: rejectDecimalOveride,
        lineCounts: model.lineCounts,
        getPercent: null
        // rejectPercentDecimals: 2
      };
      countModel.getPercent = createGetPercentMethod(countModel);
      return countModel;
    }

    function createGetPercentMethod(count: Count) {
      return () => count.lineCounts && count.lineCounts.usePercentGobs
        ? count.gobPercent : count.percent;
    }

    function calculateAggregates(count: Count): void {
      if (count.aggregateCounts) {
        count.aggregateCounts.forEach(ac => ac.calculateCount());
      }
    }

    function updateRowsAndCells(count: Count): void {
      if (count.rows) {
        let isNoCount = (count.count <= 0);
        count.rows.forEach(row => row.isNoCount = isNoCount);
      }

      if (count.cells && count.cells.length > 0) {
        count.cells.filter(c => c.affectsRowDisplay).forEach(cell => {
          cell.row.setRowsUpdateNeeded();
        });
      }
    }

    function recalculateDependencies(count: Count): void {
      if (count.showTrend || (count.rejectCounts && _.some(count.rejectCounts, x => x.showTrend))) {
        setCountTrendCounts(count);
      }
      if (count.rejectCounts) {
        count.rejectCounts.forEach(c => recalculatePercentTrend(c));
      }
      recalculatePercentTrend(count);
      calculateAggregates(count);
      updateRowsAndCells(count);
    }

    function calculateMoldCounts(model: Counts, counts: CountItem[]): boolean {
      let moldNumbers = {};
      let countChanged = false;
      let previousMoldCounts = {};
      let previousMoldMnrCounts = {};
      for (let key in model.moldCounts) {
        previousMoldCounts[key] = model.moldCounts[key].count;
        previousMoldMnrCounts[key] = model.moldMnrCounts[key].count;
        model.moldMnrCounts[key].count = 0;
        model.moldCounts[key].count = 0;
      }

      if (!model.mnrCount) {
        model.mnrCount = createSubCount(model, 0);
      }

      let previousMnrCount = model.mnrCount.count;
      model.mnrCount.count = 0;

      counts.forEach(c => {
        if (!c.moldCounts) {
          return;
        }

        for (let mold in c.moldCounts) {
          if (!moldNumbers[mold]) {
            moldNumbers[mold] = true;
          }

          let count = c.moldCounts[mold];
          if (!count.value) {
            continue;
          }

          let moldCount = model.moldCounts[mold];
          if (moldCount) {
            moldCount.count += count.value;
            if (count.mnrCount > 0) {
              model.moldMnrCounts[mold].count += count.mnrCount;
            }
          } else {
            countChanged = true;
            model.moldCounts[mold] = createSubCount(model, count.value);
            model.moldMnrCounts[mold] = createSubCount(model, count.mnrCount || 0);
          }

          if (count.mnrCount) {
            model.mnrCount.count += count.mnrCount;
          }
        }
      });

      if (model.rows && _.xor(Object.keys(moldNumbers), Object.keys(previousMoldCounts)).length > 0) {
        countChanged = true;
        model.rows.forEach(row => {
          if (!row.isMold) {
            return;
          }

          row.setRowsUpdateNeeded();
        });
      }

      if (previousMnrCount !== model.mnrCount.count) {
        countChanged = true;
        recalculateDependencies(model.mnrCount);
      } else {
        for (let key in model.moldCounts) {
          let moldCount = model.moldCounts[key];
          if (moldCount.count !== previousMoldCounts[key]) {
            countChanged = true;
            recalculateDependencies(moldCount);
          }
          let mnrCount = model.moldMnrCounts[key];
          if (mnrCount.count !== previousMoldMnrCounts[key]) {
            countChanged = true;
            recalculateDependencies(mnrCount);
          }
        }
      }

      return countChanged;
    }

    function getShift(model: Counts, newest: boolean): CountItem[] {
      let shift: apsCommon.PlantShiftInstance;
      if (newest) {
        shift = model.shopConfig.shiftsInLast24Hours[1];
      } else {
        shift = model.shopConfig.shiftsInLast24Hours[2];
      }

      return _.filter(model.counts, 
        c => c.interval === apsEnums.CountInterval.Hour
        && c.intervalStartTime >= shift.startTime.getTime()
        && c.intervalEndTime <= shift.stopTime.getTime());
    }

    function filterCountsByInterval(model: Counts): CountItem[] {
      let startTime: number;
      let endTime: number;
      let now = moment();
      let remainderMinutes = now.minute() % 10;
      let remainderSeconds = now.second();
      let remainderMilliSeconds = now.millisecond();
      switch (model.countsInterval) {
        case apsDisplayEnums.CountDisplayInterval.RollingHour:
          endTime = moment(now)
            .subtract(remainderMinutes, 'minute')
            .subtract(remainderSeconds, 'second')
            .subtract(remainderMilliSeconds, 'millisecond')
            .toDate()
            .getTime();
          startTime = moment(endTime)
             .subtract(1, 'hour')
             .toDate()
             .getTime();
           return _.filter(model.counts, c => c.intervalStartTime >= startTime
            && c.intervalStartTime < endTime);
          
        case apsDisplayEnums.CountDisplayInterval.LastTwentyMinutes:
          endTime = moment(now)
            .subtract(remainderMinutes, 'minute')
            .subtract(remainderSeconds, 'second')
            .subtract(remainderMilliSeconds, 'millisecond')
            .toDate()
            .getTime();
          startTime = moment(endTime)
            .subtract(20, 'minute')
            .toDate()
            .getTime();
          return _.filter(model.counts, c => c.intervalStartTime >= startTime
            && c.intervalStartTime < endTime);

        case apsDisplayEnums.CountDisplayInterval.CurrentHour:
          startTime = moment().startOf('hour').toDate().getTime();
          return _.filter(model.counts, c => c.intervalStartTime >= startTime);

        case apsDisplayEnums.CountDisplayInterval.PreviousHour:
          endTime = moment().startOf('hour').toDate().getTime();
          startTime = moment(endTime).subtract(1, 'hour').toDate().getTime();
          return _.filter(model.counts, c => c.intervalStartTime >= startTime
            && c.intervalStartTime < endTime);

        case apsDisplayEnums.CountDisplayInterval.CurrentShift:
          startTime = model.shopConfig.shiftsInLast24Hours[0].startTime.getTime();
          return _.filter(model.counts, c => c.intervalStartTime >= startTime);

        case apsDisplayEnums.CountDisplayInterval.LastShift:
          let lastShift = getShift(model, true);
          return lastShift ? lastShift : [];

        case apsDisplayEnums.CountDisplayInterval.ShiftBeforeLast:
          let shiftBeforeLast = getShift(model, false);
          return shiftBeforeLast ? shiftBeforeLast : [];

        default: // apsDisplayEnums.CountDisplayInterval.Last24Hours:
          endTime = moment().startOf('hour').toDate().getTime();
          startTime = moment(endTime).subtract(1, 'days').toDate().getTime();
          return _.filter(model.counts, c => c.interval !== apsEnums.CountInterval.Shift
            && c.intervalStartTime >= startTime
            && c.intervalStartTime < endTime
          );
      }
    }

    function calculateCounts(model: Counts): boolean {
      let previousCount = model.count;
      model.filteredCounts = filterCountsByInterval(model);
      if (model.filteredCounts.length === 0) {
        model.count = 0;
      } else {
        model.count = model.filteredCounts.map(c => c.value).reduce((prev, current) => prev + current);
      }
      let countChanged = (previousCount !== model.count);
      if (countChanged) {
        recalculateDependencies(model);
        if (model.showSpeed) {
          model.speed = calcSpeed(model);
        }
      }

      if (model.hasMolds) {
        countChanged = calculateMoldCounts(model, model.filteredCounts) || countChanged;
      }

      for (let key in model.countsUpdated) {
        model.countsUpdated[key] = true;
      }

      return countChanged;
    }

    function setAggregateTrendCounts(count: AggregateCount): void {
      let finalCounts = count.finalCounts.filter(x => x !== defaultCount);
      finalCounts.filter(x => !x.trendCounts).forEach(x => setCountTrendCounts(x));
      let countTrendCounts = finalCounts
        .filter(x => x.trendCounts !== undefined)
        .map(c => c.trendCounts);
      count.trendCounts = countTrendCounts.length > 0
        ? countTrendCounts.reduce((p, c) => {
          c.forEach((v, i) => p[i] = (p[i] || 0) + v);
          return p;
        })
        : [];
    }

    function calculateAggregateCount(count: AggregateCount): void {
      let previousCount = count.count;
      if (count.countsMissing) {
        populateMissingFinalCounts(count);
      }
      count.count = _.sum(count.finalCounts.filter(c => c !== defaultCount).map(c => c.count));
      if (previousCount !== count.count) {
        recalculateDependencies(count);
      }
    }

    function addRejectCount(inspectCount: Count, rejectCount: Count): void {
      if (inspectCount.rejectCounts) {
        inspectCount.rejectCounts.push(rejectCount);
      } else {
        inspectCount.rejectCounts = [rejectCount];
      }
    }

    function addAggregateCount(count: Count, aggregateCount: AggregateCount): void {
      if (count.aggregateCounts) {
        count.aggregateCounts.push(aggregateCount);
      } else {
        count.aggregateCounts = [aggregateCount];
      }
    }

    function getAggregateFinalCount(count: AggregateCount, memberCount: Counts): Count {
      let finalCount = defaultCount;
      if (count.mold) {
        if (count.isMnr) {
          if (memberCount.moldMnrCounts) {
            finalCount = memberCount.moldMnrCounts[count.mold] || defaultCount;
          }
        } else {
          if (memberCount.moldCounts) {
            finalCount = memberCount.moldCounts[count.mold] || defaultCount;
          }
        }
      } else if (count.isMnr) {
        finalCount = memberCount.mnrCount || defaultCount;
      } else {
        finalCount = memberCount;
      }

      if (finalCount !== defaultCount) {
        addAggregateCount(finalCount, count);
      }
      return finalCount;
    }

    function setFinalCountsMissing(count: AggregateCount): void {
      count.countsMissing = _.some(count.finalCounts, x => x === defaultCount);
    }

    function populateMissingFinalCounts(count: AggregateCount): void {
      for (let i in count.finalCounts) {
        if (count.finalCounts[i] === defaultCount) {
          count.finalCounts[i] = getAggregateFinalCount(count, count.memberCounts[i]);
        }
      }
      setFinalCountsMissing(count);
    }

    function createAggregateCount(
      lineCounts: LineCounts,
      usePlantPercentPreference: boolean,
      counts: Counts[],
      mold?: number,
      isMnr?: boolean,
      showPercent?: boolean,
      showTrend?: boolean,
      inspectCount?: Count): AggregateCount {
      // rejectPercentDecimals: 1            
      let rejectDecimalOveride = 1;
      if (isPlantCheck($stateParams)) {
        rejectDecimalOveride = 2;
      }

      let rejectPercentDecimals = rejectDecimalOveride;
      if (usePlantPercentPreference && inspectCount
        && lineCounts.gobsProduced
        && lineCounts.countRejectPercent === apsEnums.CountRejectPercent.PercentOfGobsProduced) {
        inspectCount = lineCounts.gobsProduced;
        rejectPercentDecimals = rejectPercentDecimals;
      }
      let aggregateCount: AggregateCount = {
        calculateCount: null,
        showPercent: showPercent,
        showTrend: showTrend,
        mold: mold,
        isMnr: isMnr,
        count: 0,
        memberCounts: counts,
        inspectCount: inspectCount,
        finalCounts: null,
        countsMissing: false,
        percent: defaultCount.percent,
        gobPercent: defaultCount.gobPercent,
        trend: defaultCount.trend,
        rejectPercentDecimals: rejectPercentDecimals,
        lineCounts: lineCounts,
        getPercent: null
      };

      aggregateCount.getPercent = createGetPercentMethod(aggregateCount);
      aggregateCount.finalCounts = counts.map(c => getAggregateFinalCount(aggregateCount, c));

      aggregateCount.setTrendCounts = () => setAggregateTrendCounts(aggregateCount);

      aggregateCount.calculateCount = () => calculateAggregateCount(aggregateCount);
      aggregateCount.calculateCount();
      setFinalCountsMissing(aggregateCount);

      return aggregateCount;
    }

    function createCounts(
      lineCounts: LineCounts,
      usePlantPercentPreference: boolean,
      id: string,
      rawCounts: apsViewModels.CountItem[],
      countsInterval: apsDisplayEnums.CountDisplayInterval,
      shopConfig: apsViewModels.ShopConfigurationWithShifts,
      inspectCount?: Count,
      showPercent?: boolean,
      showTrend?: boolean,
      hasMolds?: boolean,
      showSpeed?: boolean): Counts {
      // rejectPercentDecimals: 1

      let rejectPercentDecimals = 1;
      if (usePlantPercentPreference && inspectCount
        && lineCounts.gobsProduced
        && lineCounts.countRejectPercent === apsEnums.CountRejectPercent.PercentOfGobsProduced) {
        inspectCount = lineCounts.gobsProduced;
        rejectPercentDecimals = 2;
      }

      let model: Counts = {
        id: id,
        counts: rawCounts ? rawCounts.map(mapCount) : [],
        filteredCounts: null,
        showPercent: showPercent,
        showTrend: showTrend,
        hasMolds: hasMolds,
        count: 0,
        inspectCount: inspectCount,
        countsInterval: countsInterval,
        showPerformance: false,
        moldCounts: hasMolds ? {} : undefined,
        moldMnrCounts: hasMolds ? {} : undefined,
        mnrCount: hasMolds ? {
          count: 0,
          inspectCount: inspectCount,
          showPercent: showPercent,
          showTrend: showTrend,
          percent: defaultCount.percent,
          gobPercent: defaultCount.gobPercent,
          trend: defaultCount.trend,
          rejectPercentDecimals: 1,
          lineCounts: lineCounts,
          getPercent: null
          // rejectPercentDecimals: 2
        } : undefined,
        countsUpdated: {},
        speed: '',
        showSpeed: showSpeed,
        percent: defaultCount.percent,
        gobPercent: defaultCount.gobPercent,
        trend: defaultCount.trend,
        rejectPercentDecimals: rejectPercentDecimals,
        shopConfig: shopConfig,
        lineCounts: lineCounts,
        getPercent: null
      };

      model.getPercent = createGetPercentMethod(model);
      if (model.mnrCount) {
        model.mnrCount.getPercent = createGetPercentMethod(model.mnrCount);
      }
      // set initial filtered counts
      model.filteredCounts = model.counts;

      let current: CountItem = null;

      model.calculateCount = (): boolean => calculateCounts(model);

      model.updateCounts = (
        counts: apsViewModels.CountItem[],
        shopConfig: apsViewModels.ShopConfigurationWithShifts) => {
        model.shopConfig = shopConfig;
        model.counts = counts ? counts.map(mapCount) : [];
        let newCurrentBucket = createCurrentBucket();
        if (current !== null && current.intervalStartTime === newCurrentBucket.intervalStartTime) {
          model.counts.push(current);
        }
        return model.calculateCount();
      };

      model.handleCount = (
        event: apsViewModels.ShopCountBatchAdded,
        count: apsViewModels.ShopCountAdded): boolean => {
        let intervalStart = event.startTime.getTime();
        if (current === null || intervalStart >= current.intervalEndTime) {
          current = createCurrentBucket();
          model.counts.push(current);
        }

        if (intervalStart < current.intervalStartTime) {
          return false;
        }

        current.value += count.count;
        current.lastEndTime = event.endTime.getTime();
        if (model.hasMolds && count.moldCounts) {
          count.moldCounts.forEach(mc => {
            if (!mc.count) {
              return;
            }

            mapMoldCount(current.moldCounts, mc);
            if (mc.mnrCount) {
              if (current.mnrCount) {
                current.mnrCount += mc.mnrCount;
              } else {
                current.mnrCount = mc.mnrCount;
              }
            }
          });
        }

        return model.calculateCount();
      };

      model.handleInterval = (
        event: apsViewModels.ShopCountIntervalBucket,
        count: apsViewModels.ShopCountIntervalAdded): boolean => {
        let intervalEnd = event.intervalEnd.getTime();
        if (_.some(model.counts, c => c.interval > event.interval && c.intervalEndTime >= intervalEnd)) {
          return false;
        }
        let matching = _.find(model.counts, (c: CountItem) =>
          c.interval === event.interval && c.intervalEndTime === intervalEnd);
        if (matching) {
          _.remove(model.counts, matching);
        }

        let intervalStart = event.intervalStart.getTime();
        switch (event.interval) {
          case apsEnums.CountInterval.Minute:
            break;
          case apsEnums.CountInterval.TenMinutes:
          case apsEnums.CountInterval.Hour:
            _.remove(model.counts, (c: CountItem) =>
              c.interval < event.interval
              && c.intervalEndTime <= intervalEnd && c.intervalStartTime >= intervalStart);
            break;
          default:
            return false;
        }
        model.counts.push(mapCount({
          intervalStart: event.intervalStart,
          intervalEnd: event.intervalEnd,
          interval: event.interval,
          value: count.value,
          moldCounts: count.moldCounts
        }));

        return model.calculateCount();
      };

      model.setTrendCounts = () => setTrendCounts(model);

      if (inspectCount && showTrend && !inspectCount.trendCounts) {
        setCountTrendCounts(inspectCount);
      }

      model.calculateCount();

      return model;
    }

    function getRowId(row: LineEquipmentRow): string {
      let id = row.name;
      if (row.mold !== null) {
        id += '-' + row.mold;
      } else if (row.isSpacer && !row.name) {
        id = 'spacer-' + row.order;
      }
      if (row.parent.id) {
        id = row.parent.id + '-' + id;
      }
      return id;
    }

    function getRowDisplayName(row: LineEquipmentRow): string {
      if (row.displayBlankName || row.isSpacer) {
        return ' ';
      }
      if (row.isMold && row.useMoldAsName) {
        if (row.mold !== null) {
          return row.mold === 0 ? 'UNR' : row.mold.toString(10);
        }
        return 'No molds read';
      }
      return row.name;
    }

    function setRowNameAndId(row: LineEquipmentRow): void {
      row.displayName = getRowDisplayName(row);
      row.id = getRowId(row);
    }

    function getOrCreateEquipmentCounts(
      lineCounts: LineCounts,
      usePlantPercentPreference: boolean,
      counts: { [id: string]: Counts[] },
      rawCounts: { [key: string]: apsViewModels.CountItem[] },
      dataPointId: string,
      countsInterval: apsDisplayEnums.CountDisplayInterval,
      shopConfig: apsViewModels.ShopConfigurationWithShifts,
      inspectCount?: Count,
      showPercent?: boolean,
      showTrend?: boolean,
      hasMolds?: boolean,
      showSpeed?: boolean): Counts {

      if (!dataPointId) {
        return null;
      }

      let dpCount: Counts;
      let dpCounts = counts[dataPointId];
      if (dpCounts) {
        for (let c in dpCounts) {
          let dpCount = dpCounts[c];
          if (inspectCount) {
            if (dpCount.inspectCount === inspectCount
              && dpCount.showPercent === showPercent
              && dpCount.showTrend === showTrend
              && dpCount.hasMolds === hasMolds
              && dpCount.showSpeed === showSpeed) {
              return dpCount;
            }
          } else if (!dpCount.inspectCount && dpCount.hasMolds === hasMolds) {
            return dpCount;
          }
        }
      }

      dpCount = createCounts(lineCounts, usePlantPercentPreference, dataPointId,
        rawCounts ? rawCounts[dataPointId] : [],
        countsInterval,
        shopConfig,
        inspectCount,
        showPercent,
        showTrend,
        hasMolds,
        showSpeed);

      if (dpCounts) {
        dpCounts.push(dpCount);
      } else {
        counts[dataPointId] = [dpCount];
      }
      return dpCount;
    }

    function getCountForRowCell(
      row: LineEquipmentRow,
      cellCount: Counts): Count {
      if (row.isMold || row.parent.isMold) {
        let mold = row.mold;
        if (mold === null && row.parent.isMold) {
          mold = row.parent.mold;
        }
        if (mold === null) {
          return defaultCount;
        } else if (row.isMNR) {
          let mnrCount = cellCount.moldMnrCounts[mold];
          return mnrCount || defaultCount;
        } else {
          let moldCount = cellCount.moldCounts[mold];
          return moldCount || defaultCount;
        }
      } else if (row.isMNR) {
        if (row.parent.isMold && row.parent.mold !== null) {
          let mnrCount = cellCount.moldMnrCounts[row.parent.mold];
          return mnrCount || defaultCount;
        } else {
          return cellCount.mnrCount || defaultCount;
        }
      }

      return cellCount;
    }

    function populateCell(
      cell: LineEquipmentCell,
      row: LineEquipmentRow,
      lineCounts: LineCounts,
      cellCount: Counts | Counts[]): void {
      if (!cellCount || (cell.count && cell.count !== defaultCount)) {
        return;
      }

      let cellCounts = <Counts[]>cellCount;
      if ((row.isMold || row.parent.isMold) && !(row.mold || row.parent.mold)) {
        cell.count = defaultCount;
      } else if (cellCounts.length > 0) {
        cell.count = createAggregateCount(lineCounts, false, cellCounts, row.mold || row.parent.mold, row.isMNR,
          cellCounts[0].showPercent, cellCounts[0].showTrend, row.count);
      } else {
        cell.count = getCountForRowCell(row, <Counts>cellCount);
      }

      if (cell.count !== defaultCount) {
        if (cell.count.showPercent || cell.count.showTrend) {
          addRejectCount(row.count, cell.count);
        }

        if (cell.count.cells) {
          cell.count.cells.push(cell);
        } else {
          cell.count.cells = [cell];
        }
      }
    }

    function getCellCounts(
      row: LineEquipmentRow,
      tab: LineEquipmentTab,
      lineCounts: LineCounts,
      counts: { [id: string]: Counts[] },
      rawCounts: { [key: string]: apsViewModels.CountItem[] },
      countsInterval: apsDisplayEnums.CountDisplayInterval,
      shopConfig: apsViewModels.ShopConfigurationWithShifts): any {
      return row.cells.map((cell, c) => {
        if (cell.isFixed) {
          return null;
        }

        let column = tab.columns[c];
        if (cell.dataPointIds) {
          return cell.dataPointIds.map(id => getOrCreateEquipmentCounts(lineCounts, false, counts, rawCounts,
            id, countsInterval, shopConfig, row.count,
            column.showPercent, column.showTrend, row.isMold || row.isMNR || row.parent.isMold));
        }

        return getOrCreateEquipmentCounts(lineCounts, false, counts, rawCounts,
          cell.dataPointId, countsInterval, shopConfig, row.count,
          column.showPercent, column.showTrend, row.isMold || row.isMNR || row.parent.isMold);
      });
    }

    function populateRowChildren(
      row: LineEquipmentRow,
      tab: LineEquipmentTab,
      lineCounts: LineCounts,
      counts: { [id: string]: Counts[] },
      rawCounts: { [key: string]: apsViewModels.CountItem[] },
      countsInterval: apsDisplayEnums.CountDisplayInterval,
      shopConfig: apsViewModels.ShopConfigurationWithShifts): void {
      if (!row.children || row.children.length === 0) {
        return;
      }

      if (row.isMold && !row.mold) {
        return;
      }

      let rowChildren = [];
      row.children.forEach(childRow => {
        childRow.isChild = true;
        childRow.parent = row;
        if (childRow.isSpacer) {
          setRowNameAndId(childRow);
          rowChildren.push(childRow);
          return;
        }

        let rowRows = createRowRows(childRow, tab, lineCounts, counts, rawCounts, countsInterval, shopConfig);
        rowRows.forEach(r => rowChildren.push(r));
      });

      row.expandedChildren = rowChildren;
    }

    function populateRow(
      row: LineEquipmentRow,
      tab: LineEquipmentTab,
      lineCounts: LineCounts,
      counts: { [id: string]: Counts[] },
      rawCounts: { [key: string]: apsViewModels.CountItem[] },
      countsInterval: apsDisplayEnums.CountDisplayInterval,
      shopConfig: apsViewModels.ShopConfigurationWithShifts): void {
      if (row.count) {
        if (row.count.rows) {
          row.count.rows.push(row);
        } else {
          row.count.rows = [row];
        }
      }

      row.cells.forEach((cell, c) => {
        cell.row = row;
        if (cell.isFixed) {
          return;
        }

        let cellCount = row.cellCounts[c];
        if (!cellCount) {
          return;
        }

        if (row.hideIfCellIndexZero === c
          || row.orderByCellIndex === c
          || ((row.hasMNR || row.parent.hasMNR) && row.isMNR)) {
          cell.affectsRowDisplay = true;
        }

        populateCell(cell, row, lineCounts, cellCount);
      });

      populateRowChildren(row, tab, lineCounts, counts, rawCounts, countsInterval, shopConfig);
    }

    function copyCell(c: LineEquipmentCell, row: LineEquipmentRow): LineEquipmentCell {
      return {
        count: undefined,
        fixedValue: c.fixedValue,
        isFixed: c.isFixed,
        type: c.type,
        dataPointId: c.dataPointId,
        dataPointIds: c.dataPointIds,
        row: row,
        affectsRowDisplay: c.affectsRowDisplay,
        rightJustify: c.rightJustify
      };
    }

    function setRowsUpdateNeeded(row: LineEquipmentRow): void {
      let parent = row.parent;
      while (parent.parent) {
        parent = parent.parent;
      }
      parent.needsRowUpdate = true;
    }

    function copyRow(row: LineEquipmentRow, parent: RowParent): LineEquipmentRow {
      let copiedRow: LineEquipmentRow = {
        name: row.name,
        isMold: row.isMold,
        inspectDataPointId: row.inspectDataPointId,
        inspectDataPointIds: row.inspectDataPointIds,
        cells: null,
        children: null,
        hasMNR: row.hasMNR,
        isMNR: row.isMNR,
        isSpacer: row.isSpacer,
        mold: row.mold,
        useMoldAsName: row.useMoldAsName,
        parent: parent,
        cellCounts: row.cellCounts,
        count: row.count,
        displayBlankName: row.displayBlankName,
        hideIfCellIndexZero: row.hideIfCellIndexZero,
        order: row.order,
        orderByCellIndex: row.orderByCellIndex,
        orderByCellIndexDescending: row.orderByCellIndexDescending,
        isChild: row.isChild,
        useFullInspectCount: row.useFullInspectCount,
        headerRow: row.headerRow,
        isNoCount: false,
        displayName: null,
        expandedChildren: null,
        outputRows: null,
        setRowsUpdateNeeded: null
      };
      copiedRow.setRowsUpdateNeeded = () => setRowsUpdateNeeded(copiedRow);
      copiedRow.cells = row.cells.map(c => copyCell(c, copiedRow));
      if (row.children) {
        copiedRow.children = row.children.map(c => copyRow(c, copiedRow));
      }
      return copiedRow;
    }

    function createMoldRow(
      row: LineEquipmentRow,
      lineCounts: LineCounts,
      mold: number): LineEquipmentRow {
      let moldRow = copyRow(row, row.parent);
      if (row.isChild || row.useFullInspectCount) {
        moldRow.count = row.count;
      } else if (row.inspectDataPointIds) {
        let aggregateCount = <AggregateCount>row.count;
        let counts = <Counts[]>aggregateCount.memberCounts;
        moldRow.count = createAggregateCount(lineCounts, false, counts, mold, row.isMNR);
      } else {
        moldRow.count = row.count.moldCounts[mold];
      }
      moldRow.mold = mold;
      setRowNameAndId(moldRow);
      return moldRow;
    }

    function getRowMoldNumbers(row: LineEquipmentRow): number[] {
      let moldNumbers = [];
      if (row.count.moldCounts) {
        moldNumbers = Object.keys(row.count.moldCounts);
      } else if (row.count.memberCounts) {
        row.count.memberCounts.forEach(c => {
          if (c.moldCounts) {
            moldNumbers = moldNumbers.concat(Object.keys(c.moldCounts));
          }
        });
      }

      row.cellCounts.forEach(c => {
        if (!c) {
          return;
        }
        if (c.moldCounts) {
          moldNumbers = moldNumbers.concat(Object.keys(c.moldCounts));
        } else if (c.length > 0) {
          c.forEach(count => {
            if (count.moldCounts) {
              moldNumbers = moldNumbers.concat(Object.keys(count.moldCounts));
            }
          });
        }
      });
      return _.uniq(moldNumbers).map(x => parseInt(x, 10));
    }

    function idsMatch(ids1: string[], ids2: string[]): boolean {
      return ids1 && ids2 && _.xor(ids1, ids2).length === 0;
    }

    function setRowCountFromParent(row: LineEquipmentRow): void {
      if (!row.parent.cells) {
        row.count = defaultCount;
      }

      let rowCountCell = _.find(row.parent.cells, c =>
        (row.inspectDataPointId && c.dataPointId === row.inspectDataPointId)
        || idsMatch(row.inspectDataPointIds, c.dataPointIds));
      if (rowCountCell) {
        row.count = rowCountCell.count;
      } else {
        row.count = defaultCount;
      }
    }

    function createRowRows(
      row: LineEquipmentRow,
      tab: LineEquipmentTab,
      lineCounts: LineCounts,
      counts: { [id: string]: Counts[] },
      rawCounts: { [key: string]: apsViewModels.CountItem[] },
      countsInterval: apsDisplayEnums.CountDisplayInterval,
      shopConfig: apsViewModels.ShopConfigurationWithShifts): LineEquipmentRow[] {

      if (row.parent.cells) {
        setRowCountFromParent(row);
      } else {
        if (row.inspectDataPointIds) {
          let inspectCounts = row.inspectDataPointIds.map(id => getOrCreateEquipmentCounts(lineCounts, false,
            counts, rawCounts, id, countsInterval, shopConfig, null, false, false,
            row.isMold || row.isMNR || row.parent.isMold));
          row.count = createAggregateCount(lineCounts, false, inspectCounts);
        } else {
          row.count = getOrCreateEquipmentCounts(lineCounts, false, counts, rawCounts,
            row.inspectDataPointId, countsInterval, shopConfig, null, false, false,
            row.isMold || row.isMNR || row.parent.isMold);
        }
      }

      row.cellCounts = getCellCounts(row, tab, lineCounts, counts, rawCounts, countsInterval, shopConfig);
      row.setRowsUpdateNeeded = () => setRowsUpdateNeeded(row);
      setRowNameAndId(row);
      let rowRows = [row];
      if (row.isMold && row.mold === null) {
        let moldNumbers = getRowMoldNumbers(row);

        if (moldNumbers.length > 0) {
          moldNumbers.forEach(mold => {
            let moldRow = createMoldRow(row, lineCounts, mold);
            rowRows.push(moldRow);
          });
        }
      }

      rowRows.forEach(rowRow => populateRow(rowRow, tab, lineCounts, counts, rawCounts, countsInterval, shopConfig));
      return rowRows;
    }

    function cellsHaveMNR(row: LineEquipmentRow): boolean {
      return _.some(row.cells, x => x.count && x.count.count > 0);
    }

    function rowHasMNR(row: LineEquipmentRow, childRows: LineEquipmentRow[]): boolean {
      if (row.isMNR) {
        return cellsHaveMNR(row);
      }

      if (childRows.length === 0) {
        return false;
      }

      let mnrChildRow = _.find(childRows, x => x.isMNR);
      if (!mnrChildRow) {
        return false;
      }

      return cellsHaveMNR(mnrChildRow);
    }

    function getRowOrder(r: LineEquipmentRow): number {
      let order = r.order * 1000000;

      if (r.orderByCellIndex === null
        || r.cells.length <= r.orderByCellIndex) {
        if (r.isMold && r.mold > 0) {
          order += r.mold;
        }
        return order;
      }

      let count = r.cells[r.orderByCellIndex].count;
      if (!count) {
        return order;
      }

      if (r.orderByCellIndexDescending) {
        return order - count.count;
      }
      return order + count.count;
    }

    function setSortOrder(row: LineEquipmentRow): void {
      row.sortOrder = getRowOrder(row);
    }

    function sortRows(rows: LineEquipmentRow[]): LineEquipmentRow[] {
      return _.sortBy(rows, 'sortOrder');
    }

    function updateRow(row: LineEquipmentRow, lineCounts: LineCounts,
      tab: LineEquipmentTab): LineEquipmentRow[] {
      let rowRows = [];
      if (row.isSpacer) {
        setSortOrder(row);
        return [row];
      }

    
      if(row.parent.expandedChildren == null || row.parent.expandedChildren.filter == null)
      {
        return;
      }

      let expandedRows = row.parent.expandedChildren.filter(c =>
        ((!row.inspectDataPointId && !c.inspectDataPointId)
          || (row.inspectDataPointId && c.inspectDataPointId === row.inspectDataPointId)
          || idsMatch(row.inspectDataPointIds, c.inspectDataPointIds))
        && ((row.isMold && c.isMold) || c.name === row.name));

      if (row.isMold && row.mold === null) {
        let noMoldRow = expandedRows[0];
        let moldNumbers = getRowMoldNumbers(noMoldRow);
        moldNumbers.forEach(mold => {
          let moldRow = _.find(expandedRows, { mold: mold });
          if (!moldRow) {
            moldRow = createMoldRow(noMoldRow, lineCounts, mold);
            expandedRows.push(moldRow);
            row.parent.expandedChildren.push(moldRow);
          }
        });

        let toRemove = _.xor(moldNumbers, expandedRows.filter(x => x.mold !== null).map(x => x.mold));
        toRemove.forEach(mold => {
          let removeRow = _.remove(expandedRows, { mold: mold });
          _.remove(row.parent.expandedChildren, removeRow);
        });
      }

      if (row.isMold && row.mold !== null && expandedRows.length === 1) {
        return expandedRows;
      }

      expandedRows.forEach(rowRow => {
        if (rowRow.isMold && rowRow.mold === null) {
          return;
        }

        if (rowRow.count === defaultCount) {
          setRowCountFromParent(rowRow);
        }

        if (rowRow.count !== defaultCount) {
          rowRow.cells.forEach((cell, c) => populateCell(cell, rowRow, lineCounts, rowRow.cellCounts[c]));
        }

        if (rowRow.hideIfCellIndexZero !== null && rowRow.cells.length > rowRow.hideIfCellIndexZero) {
          let cellCount = rowRow.cells[rowRow.hideIfCellIndexZero].count;
          if (!cellCount || cellCount.count === 0) {
            return;
          }
        }

        if (rowRow.children && !rowRow.expandedChildren) {
          populateRowChildren(rowRow, tab, lineCounts, tab.counts.counts, null, null, null);
        }

        let childRowRows: LineEquipmentRow[] = [];
        if (rowRow.children) {
          rowRow.children.forEach(childRow => {
            let childRows = updateRow(childRow, lineCounts, tab);
            childRowRows.push(...childRows);
          });
        }

        if (rowRow.hasMNR && !rowHasMNR(rowRow, childRowRows)) {
          return;
        }

        if (childRowRows.length > 0) {
          rowRow.outputRows = sortRows(childRowRows);
        }

        setSortOrder(rowRow);
        rowRows.push(rowRow);
      });

      return rowRows;
    }

    function createOutputRows(rows: LineEquipmentRow[]): LineEquipmentRow[] {
      let outputRows = [];
      rows.forEach(row => {
        outputRows.push(row);
        if (row.outputRows) {
          let childOutputRows = createOutputRows(row.outputRows);
          outputRows.push(...childOutputRows);
        }
      });
      return outputRows;
    }

    function setOutputRows(tab: LineEquipmentTab, outputRows: LineEquipmentRow[]): void {
      if (!tab.outputRows || tab.outputRows.length === 0) {
        tab.outputRows = outputRows;
        return;
      }

      let existingIds = tab.outputRows.map(x => x.id);
      let newIds = outputRows.map(x => x.id);
      let removeIds = _.difference(existingIds, newIds);
      if (removeIds.length > 0) {
        removeIds.forEach(id => _.remove(tab.outputRows, { id: id }));
      }

      for (let i = 0; i < outputRows.length; i++) {
        let newRow = outputRows[i];
        let index = _.findIndex(tab.outputRows, { id: newRow.id });
        if (index < 0) {
          tab.outputRows.splice(i, 0, newRow);
        } else if (index !== i) {
          let row = tab.outputRows.splice(index, 1)[0];
          tab.outputRows.splice(i, 0, row);
        }
      }
    }

    function updateTabRows(tab: LineEquipmentTab, lineCounts: LineCounts): void {
      let tabRows = [];
      tab.needsRowUpdate = false;
      tab.rows.forEach(row => {
        let rowRows = updateRow(row, lineCounts, tab);
        tabRows.push(...rowRows);
      });
      tabRows = sortRows(tabRows);

      setOutputRows(tab, createOutputRows(tabRows));
      tab.needsRowUpdate = false;
    }

    function populateTab(
      counts: LineEquipmentCounts,
      tab: LineEquipmentTab,
      lineCounts: LineCounts,
      rawCounts: { [key: string]: apsViewModels.CountItem[] },
      countsInterval: apsDisplayEnums.CountDisplayInterval,
      shopConfig: apsViewModels.ShopConfigurationWithShifts): void {
      let tabChildren = [];

      tab.counts = counts;
      tab.rows.forEach(row => {
        row.parent = tab;
        if (row.isSpacer) {
          setRowNameAndId(row);
          tabChildren.push(row);
          return;
        }

        let rowRows = createRowRows(row, tab, lineCounts, counts.counts, rawCounts, countsInterval, shopConfig);
        rowRows.forEach(r => tabChildren.push(r));
      });

      tab.expandedChildren = tabChildren;
      tab.updateRows = () => updateTabRows(tab, lineCounts);
      tab.updateRows();
    }

    function populateCounts(
      equipment: LineEquipmentItem,
      rawCounts: { [key: string]: apsViewModels.CountItem[] },
      countsInterval: apsDisplayEnums.CountDisplayInterval,
      shopConfig: apsViewModels.ShopConfigurationWithShifts,
      lineCounts: LineCounts): { [id: string]: Counts[] } {

      let counts: { [id: string]: Counts[] } = {};

      equipment.inspectCount = getOrCreateEquipmentCounts(lineCounts, true, counts, rawCounts,
        equipment.inspectionDataPointId, countsInterval, shopConfig,
        null, null, null, null, true);
      if (equipment.rejectDataPointId) {
        equipment.rejectCount = getOrCreateEquipmentCounts(lineCounts, true, counts, rawCounts,
          equipment.rejectDataPointId, countsInterval, shopConfig,
          equipment.inspectCount, true, true);

        let inspectCount = lineCounts.gobsProduced
          && lineCounts.countRejectPercent === apsEnums.CountRejectPercent.PercentOfGobsProduced
          ? lineCounts.gobsProduced : equipment.inspectCount;
        addRejectCount(inspectCount, equipment.rejectCount);
      }

      return counts;
    }

    function handleEquipmentCounts(
      event: apsViewModels.ShopCountBatchAdded,
      counts: apsViewModels.ShopCountAdded[],
      equipmentCounts: { [id: string]: Counts[] }): boolean {
      let updated = false;
      counts.forEach(count => {
        let dpCounts = equipmentCounts[count.dataPointId];
        if (!dpCounts) {
          return;
        }

        dpCounts.forEach(c => {
          updated = c.handleCount(event, count) || updated;
        });
      });
      return updated;
    }

    function handleEquipmentIntervals(
      event: apsViewModels.ShopCountIntervalBucket,
      counts: apsViewModels.ShopCountIntervalAdded[],
      equipmentCounts: { [id: string]: Counts[] }): boolean {
      let updated = false;
      counts.forEach(count => {
        let dpCounts = equipmentCounts[count.dataPointId];
        if (!dpCounts) {
          return;
        }

        dpCounts.forEach(c => {
          updated = c.handleInterval(event, count) || updated;
        });
      });
      return updated;
    }

    function updateEquipmentCounts(
      counts: { [key: string]: apsViewModels.CountItem[] },
      equipmentCounts: { [id: string]: Counts[] },
      shopConfig: apsViewModels.ShopConfigurationWithShifts): boolean {
      let updated = false;
      for (let key in equipmentCounts) {
        equipmentCounts[key].forEach(c => {
          updated = c.updateCounts(counts[key], shopConfig) || updated;
        });
      }
      return updated;
    }

    function handleEquipmentIntervalChanged(
      changedInterval: apsDisplayEnums.CountDisplayInterval,
      counts: { [id: string]: Counts[] }) {
      for (let dataPointId in counts) {
        let equipCounts = counts[dataPointId];
        equipCounts.forEach(count => {
          count.countsInterval = changedInterval;
          count.calculateCount();
        });
      }
    }

    function setHealthCssClasses(equipment: LineEquipmentItem): void {
      let cssPrefix;
      let cssLinePrefix;
      if (equipment.equipmentHealthState) {
        let stateName = apsEnums.EquipmentHealthState[equipment.equipmentHealthState].toLowerCase();
        let lineStateName = equipment.equipmentHealthState === apsEnums.EquipmentHealthState.Good
          && equipment.hasInconsistentConfiguration
          ? apsEnums.EquipmentHealthState[apsEnums.EquipmentHealthState.Warning].toLowerCase()
          : stateName;
        cssPrefix = 'equip-status-' + stateName + '-';
        cssLinePrefix = 'equip-status-' + lineStateName + '-';
      } else {
        cssPrefix = 'equip-status-unknown-';
        cssLinePrefix = 'equip-status-unknown-';
      }
      equipment.lineHealthCssBox = cssLinePrefix + 'box';
      equipment.healthCssBox = cssPrefix + 'box';
      equipment.healthCssText = cssPrefix + 'text';
    }

    function handleInconsistencyChanged(
      event: apsViewModels.InconsistentEquipmentConfigurationChanged,
      equipment: { [id: string]: LineEquipmentItem }): void {
      let type = equipmentService.getLineEquipmentType(event.equipmentType);
      if (!type) {
        return;
      }

      for (let id in equipment) {
        if (equipment[id].type === type) {
          equipment[id].hasInconsistentConfiguration = event.isDetected;
          setHealthCssClasses(equipment[id]);
        }
      }
    }

    function setEquipment(
      equipment: LineEquipmentItem,
      rawCounts: { [key: string]: apsViewModels.CountItem[] },
      countsInterval: apsDisplayEnums.CountDisplayInterval,
      line: LineItem,
      inconsistencies: apsEnums.EquipmentType[],
      lineCounts: LineCounts): void {

      equipment.line = line;
      equipment.counts = populateCounts(equipment, rawCounts, countsInterval, lineCounts.shopConfig, lineCounts);
      equipment.hasInconsistentConfiguration = _.some(inconsistencies, i => i === equipment.equipmentType);

      setHealthCssClasses(equipment);

      equipment.handleCount = (
        event: apsViewModels.ShopCountBatchAdded,
        counts: apsViewModels.ShopCountAdded[]) =>
        handleEquipmentCounts(event, counts, equipment.counts);

      equipment.handleInterval = (
        event: apsViewModels.ShopCountIntervalBucket,
        counts: apsViewModels.ShopCountIntervalAdded[]) =>
        handleEquipmentIntervals(event, counts, equipment.counts);

      equipment.handleEquipmentHealthUpdate = (event: apsViewModels.EquipmentHealthUpdated) => {
        equipment.equipmentHealthState = event.equipmentHealthState;
        equipment.equipmentLastHealthStateUpdate = event.eventDate;
        equipment.equipmentHealthStateDetail = event.detail;
        setHealthCssClasses(equipment);
      };

      equipment.updateCounts = (
        counts: { [key: string]: apsViewModels.CountItem[] },
        shopConfig: apsViewModels.ShopConfigurationWithShifts) =>
        updateEquipmentCounts(counts, equipment.counts, shopConfig);

      equipment.handleIntervalChange = (changedInterval: apsDisplayEnums.CountDisplayInterval) => {
        handleEquipmentIntervalChanged(changedInterval, equipment.counts);
      };
    }

    function populateEquipment(
      lineCounts: LineCounts,
      rawCounts: { [key: string]: apsViewModels.CountItem[] },
      countsInterval: apsDisplayEnums.CountDisplayInterval,
      canDisable: boolean,
      inconsistencies: apsEnums.EquipmentType[]): { [id: string]: LineEquipmentItem } {

      let equipments: { [id: string]: LineEquipmentItem } = {};
      lineCounts.lines.forEach(line => {
        if (!canDisable) {
          line.equipment = _.filter(line.equipment, { isDisabled: false });
        }
        line.equipment = _.sortBy(line.equipment, 'order');
        line.equipment.forEach(equipment => {
          setEquipment(equipment, rawCounts, countsInterval, line, inconsistencies, lineCounts);
          equipments[equipment.id] = equipment;
        });
      });
      return equipments;
    }

    function updateRows(tabs: LineEquipmentTab[]): void {
      tabs.filter(t => t.needsRowUpdate).forEach(t => t.updateRows());
    }

    function createLineEquipmentDetails(
      model: LineEquipmentCounts,
      lineCounts: LineCounts,
      equipmentCounts: { [id: string]: Counts[] },
      details: apsViewModels.LineEquipmentDataPointItem[],
      rawCounts: { [key: string]: apsViewModels.CountItem[] },
      countsInterval: apsDisplayEnums.CountDisplayInterval,
      equipment: LineEquipmentItem,
      group: LineEquipmentGroup): void {
      model.details = [];
      let groupedDetails = _.groupBy(details, 'name');
      for (let name in groupedDetails) {
        let points = groupedDetails[name];
        let point = points[0];
        let order = point.order;
        let hasInspect = point.type === apsEnums.DataPointType.Reject;
        let inspectCount = hasInspect ? (group ? group.inspectCount : equipment.inspectCount) : null;
        let count: Count;
        let counts = points.map(p =>
          getOrCreateEquipmentCounts(lineCounts, true, equipmentCounts,
            rawCounts, p.dataPointId, countsInterval, lineCounts.shopConfig, inspectCount,
            hasInspect, hasInspect));
        if (counts.length === 1) {
          count = counts[0];
        } else {
          count = createAggregateCount(lineCounts, true, counts, null, null, hasInspect, hasInspect, inspectCount);
          order = _.max(_.map(points, 'order'));
        }
        model.details.push({ name: name, count: count, order: order });
      }
      model.details = _.sortBy(model.details, ['order', 'name']);
    }

    function createLineEquipmentCounts(
      tabs: apsViewModels.LineEquipmentTab[],
      lineCounts: LineCounts,
      details: apsViewModels.LineEquipmentDataPointItem[],
      rawCounts: { [key: string]: apsViewModels.CountItem[] },
      countsInterval: apsDisplayEnums.CountDisplayInterval,
      equipment: LineEquipmentItem,
      group: LineEquipmentGroup): LineEquipmentCounts {
      let equipmentTabs = <LineEquipmentTab[]>tabs;
      let equipmentCounts: { [id: string]: Counts[] } = {};

      let lineEquipmentIds = equipment ? [equipment.id] : Object.keys(group.equipment);

      let model: LineEquipmentCounts = {
        tabs: equipmentTabs,
        details: null,
        counts: equipmentCounts,
        onUpdated: [],
        handleCount: null,
        handleInterval: null,
        updateCounts: null,
        handleIntervalChange: null,
        equipment: equipment,
        group: group
      };

      if (equipmentTabs) {
        equipmentTabs.forEach(tab => populateTab(model, tab, lineCounts, rawCounts, countsInterval, lineCounts.shopConfig));
      }
      if (details && details.length > 0) {
        createLineEquipmentDetails(model, lineCounts, equipmentCounts, details,
          rawCounts, countsInterval, equipment, group);
      }

      let handleUpdate = () => {
        if (model.onUpdated.length > 0) {
          model.onUpdated.forEach(f => f());
        }
      };

      model.handleCount = (
        event: apsViewModels.ShopCountBatchAdded,
        counts: apsViewModels.ShopCountAdded[]): boolean => {
        counts = counts.filter(c => _.some(lineEquipmentIds, id => c.lineEquipmentId === id));
        if (counts.length === 0) {
          return false;
        }
        if (handleEquipmentCounts(event, counts, equipmentCounts)) {
          if (equipmentTabs) {
            updateRows(equipmentTabs);
          }
          handleUpdate();
          return true;
        }
        return false;
      };

      model.handleInterval = (
        event: apsViewModels.ShopCountIntervalBucket,
        counts: apsViewModels.ShopCountIntervalAdded[]): boolean => {
        counts = counts.filter(c => _.some(lineEquipmentIds, id => c.lineEquipmentId === id));
        if (counts.length === 0) {
          return false;
        }
        if (handleEquipmentIntervals(event, counts, equipmentCounts)) {
          if (equipmentTabs) {
            updateRows(equipmentTabs);
          }
          handleUpdate();
          return true;
        }
        return false;
      };

      model.updateCounts = (
        counts: { [key: string]: apsViewModels.CountItem[] },
        shopConfig: apsViewModels.ShopConfigurationWithShifts): boolean => {
        if (updateEquipmentCounts(counts, equipmentCounts, shopConfig)) {
          handleUpdate();
          return true;
        }
        return false;
      };

      model.handleIntervalChange = (changedInterval: apsDisplayEnums.CountDisplayInterval) => {
        handleEquipmentIntervalChanged(changedInterval, equipmentCounts);
        handleUpdate();
      };

      return model;
    }

    function createEquipmentGroup(id: number, name: string, type: apsEnums.LineEquipmentType): LineEquipmentGroup {
      return {
        lineEquipmentNameIds: [id],
        types: [type],
        names: [name],
        name: name,
        equipment: {},
        inspectCount: null,
        hasRejects: false,
        lineLineEquipmentTabs: null,
        lineStartJunctionId: null
      };
    }

    function populateEquipmentGroups(lineCounts: LineCounts) {
      let groups: LineEquipmentGroup[] = [];
      Object.keys(lineCounts.equipment).forEach(key => {
        let e = lineCounts.equipment[key];
        let group = _.find(groups, x => _.some(x.lineEquipmentNameIds, id => id === e.lineEquipmentNameId));
        if (!group
          && (e.type === apsEnums.LineEquipmentType.CheckDetector
            || e.type === apsEnums.LineEquipmentType.HEYE)) {
          if (e.type === apsEnums.LineEquipmentType.CheckDetector) {
            group = _.find(groups, x => _.some(x.types, t => t === apsEnums.LineEquipmentType.HEYE));
          } else if (e.type === apsEnums.LineEquipmentType.HEYE) {
            group = _.find(groups, x => _.some(x.types, t => t === apsEnums.LineEquipmentType.CheckDetector));
          }

          if (group) {
            group.lineEquipmentNameIds.push(e.lineEquipmentNameId);
            group.types.push(e.type);
            group.names.push(e.name);
            group.name = group.name + '/' + e.name;
          }
        }

        if (!group) {
          group = createEquipmentGroup(e.lineEquipmentNameId, e.name, e.type);
          groups.push(group);
        }

        group.equipment[e.id] = e;
      });

      groups.forEach(group => {
        let inspectCounts = [];
        let rejectCounts = [];
        for (let key in group.equipment) {
          let equipment = group.equipment[key];
          inspectCounts.push(equipment.inspectCount);
          if (equipment.rejectDataPointId) {
            rejectCounts.push(equipment.rejectCount);
          }
        }
        if (inspectCounts.length === 1) {
          group.inspectCount = inspectCounts[0];
        } else {
          group.inspectCount = createAggregateCount(lineCounts, false, inspectCounts);
        }
        if (rejectCounts.length > 0) {
          group.hasRejects = true;
          if (rejectCounts.length === 1) {
            group.rejectCount = rejectCounts[0];
          } else {
            group.rejectCount = createAggregateCount(lineCounts, true, rejectCounts,
              null, null, true, true,(lineCounts.isCalcGroupByShears)? lineCounts.gobsProduced: group.inspectCount);
          }

          let inspectCount = lineCounts.gobsProduced
            && lineCounts.countRejectPercent === apsEnums.CountRejectPercent.PercentOfGobsProduced
            ? lineCounts.gobsProduced : group.inspectCount;
          addRejectCount(inspectCount, group.rejectCount);


          if(group.rejectCount && lineCounts.isCalcGroupByShears && group.rejectCount && lineCounts.gobsProduced){
            group.rejectCount.percent = calcPercentStrTwoDecimals(group.rejectCount.count, lineCounts.gobsProduced.count);
          }

       

        }
      });

      return groups;
    }

     function populateEquipmentGroupsV1(lineCounts: LineCounts) {
      let groups: LineEquipmentGroup[] = [];
      let aieGroups: LineEquipmentGroup[] = [];
      let aieFirst: LineEquipmentGroup;
      
      Object.keys(lineCounts.equipment).forEach(key => {
        let e = lineCounts.equipment[key];
        
        let group = _.find(groups, x => _.some(x.lineEquipmentNameIds, id => id === e.lineEquipmentNameId));
        if (!group
          && (e.type === apsEnums.LineEquipmentType.CheckDetector
            || e.type === apsEnums.LineEquipmentType.HEYE)) {
          if (e.type === apsEnums.LineEquipmentType.CheckDetector) {
            group = _.find(groups, x => _.some(x.types, t => t === apsEnums.LineEquipmentType.HEYE));
          } else if (e.type === apsEnums.LineEquipmentType.HEYE) {
            group = _.find(groups, x => _.some(x.types, t => t === apsEnums.LineEquipmentType.CheckDetector));
          }
        
          if (group) {
            group.lineEquipmentNameIds.push(e.lineEquipmentNameId);
            group.types.push(e.type);
            group.names.push(e.name);
            group.name = group.name + '/' + e.name;
          }
        }
        if (!group) {
          group = createEquipmentGroup(e.lineEquipmentNameId, e.name, e.type);
          if(e.inspectionDataPointId == lineCounts.heInId){
            group.name = 'HE';
            group.order = 1;
            group.kpiName = group.name;
            groups.push(group);
          }
          else if(e.inspectionDataPointId == lineCounts.lehrInId){
            group.name = 'LEHR';
            group.order = 2;
            group.kpiName = group.name;
            groups.push(group);
          }
          else if(e.inspectionDataPointId == lineCounts.packedCountId){
            group.name = 'PACK';
            group.order = 5;
            group.kpiName =  group.name;
            groups.push(group);
          }
          else{

          if( e.type === apsEnums.LineEquipmentType.InspectReject  ||
              e.type === apsEnums.LineEquipmentType.WSI  ||
              e.type === apsEnums.LineEquipmentType.WBF  ||
              e.type === apsEnums.LineEquipmentType.HEYE  ||
              e.type === apsEnums.LineEquipmentType.CheckDetector  ||
              e.type === apsEnums.LineEquipmentType.MX4  ||
              e.type === apsEnums.LineEquipmentType.Multi  ||
              e.type === apsEnums.LineEquipmentType.Mcal  ||
              e.type === apsEnums.LineEquipmentType.FlexBC  ||
              e.type === apsEnums.LineEquipmentType.FlexM  )
            {
              aieGroups.push(group);


              if(!aieFirst && e.inspectionDataPointId == lineCounts.firstInspectionId ){
                aieFirst = group;
                group.name = 'AIE';
                group.order = 3;
                group.kpiName = group.name;
                group.lineStartJunctionId = e.line.startJunctionId; // this is a hook for us to look for other equipment on the same junction
                groups.push(aieFirst);


                let aieOther  = createEquipmentGroup(-1, 'CE DELTA', apsEnums.LineEquipmentType.InspectReject);
                aieOther.kpiName = aieOther.name;
                aieOther.order = 4;
                groups.push(aieOther);
              }
            }
          }
        }
        group.equipment[e.id] = e;
      });
      
      groups.forEach(group => {
        let inspectCounts = [];
        let rejectCounts = [];
        for (let key in group.equipment) {
          let equipment = group.equipment[key];
          inspectCounts.push(equipment.inspectCount);
          if (equipment.rejectDataPointId) {
            rejectCounts.push(equipment.rejectCount);
          }
        }
        if (inspectCounts.length === 1) {
          group.inspectCount = inspectCounts[0];
        } else {
          group.inspectCount = createAggregateCount(lineCounts, false, inspectCounts);
        }
        if (rejectCounts.length > 0) {
          group.hasRejects = true;
          if (rejectCounts.length === 1) {
            group.rejectCount = rejectCounts[0];
          } else {
            group.rejectCount = createAggregateCount(lineCounts, true, rejectCounts,
              null, null, true, true, group.inspectCount);
          }

          let inspectCount = lineCounts.gobsProduced
            && lineCounts.countRejectPercent === apsEnums.CountRejectPercent.PercentOfGobsProduced
            ? lineCounts.gobsProduced : group.inspectCount;
          addRejectCount(inspectCount, group.rejectCount);
        }
      });
      
      populateAieGroup(lineCounts, aieGroups, aieFirst);

      /* assign first inspections and ce rejects to AIE */
      return groups;
    }

    function populateAieGroup(lineCounts: LineCounts, aieGroups: LineEquipmentGroup[], aieFirst: LineEquipmentGroup)
    {
        /* AIE = automated inspection equipment ------ BEGIN */
        let inspectCounts1 = [];
        let rejectCounts1 = [];

        if(aieFirst)
        {

          aieGroups.forEach(group => {
          
            for (let key in group.equipment) {
              let equipment = group.equipment[key];
    
              if(aieFirst.lineStartJunctionId && aieFirst.lineStartJunctionId === equipment.line.startJunctionId && equipment.order === 1)
              {
                inspectCounts1.push(equipment.inspectCount);
              }
              if (equipment.rejectDataPointId) {
                rejectCounts1.push(equipment.rejectCount);
              }
            }
            if (inspectCounts1.length === 1) {
              group.inspectCount = inspectCounts1[0];
            } else {
              group.inspectCount = createAggregateCount(lineCounts, false, inspectCounts1);
            }
            if (rejectCounts1.length > 0) {
              group.hasRejects = true;
              if (rejectCounts1.length === 1) {
                group.rejectCount = rejectCounts1[0];
              } else {
                group.rejectCount = createAggregateCount(lineCounts, true, rejectCounts1,
                  null, null, true, true, group.inspectCount);
              }
    
              let inspectCount = group.inspectCount;
              addRejectCount(inspectCount, group.rejectCount);
            }
          });
          /* AIE = automated inspection equipment ------ END */

          aieFirst.inspectCount =  createAggregateCount(lineCounts, false, inspectCounts1);
          aieFirst.rejectCount =  createAggregateCount(lineCounts, false, rejectCounts1);
      }
    }

    function reCalcGroupPercents(lineCounts: LineCounts)
    {
      let groups = lineCounts.equipmentGroupsV1;
      
      let he = _.find(groups, x => x.name === 'HE');
      let lehr = _.find(groups, x => x.name === 'LEHR');
      let aie = _.find(groups, x => x.name === 'AIE');
      let aieOther = _.find(groups, x => x.name === 'CE DELTA');
      let ptp = _.find(groups, x => x.name === 'PACK');

    if(he && lehr && aie && ptp)
    {
          let shears = (he != null && he.inspectCount != null && he.inspectCount.count != null) ? he.inspectCount.count : 0;
          let heReject = (he != null && he.rejectCount != null && he.rejectCount.count != null) ? he.rejectCount.count : 0;
          let lehrIn = (lehr != null && lehr.inspectCount != null && lehr.inspectCount != null) ? lehr.inspectCount.count: 0;
          let lehrOut = (aie != null && aie.inspectCount != null && aie.inspectCount.count != null)? aie.inspectCount.count: 0;
          let ceReject =(aie != null && aie.rejectCount != null && aie.rejectCount.count != null)? aie.rejectCount.count: 0;
          let eol = (ptp != null && ptp.inspectCount != null && ptp.inspectCount.count != null)? ptp.inspectCount.count: 0;

          // calc hot end
          let heOut = shears - heReject || 0;
          he.inspectCount.percent = calcPercentStr(heOut, shears);
          he.rejectCount.percent  = calcPercentFromOneStr(heOut, shears);
          he.wareLossPercent = calcPercentFromOneStr(lehrIn, shears);
          he.efficiencyAgainstShears = calcPercentStr(lehrIn, shears);

          // calc lehr
          let lehrReject = lehrIn - lehrOut;
          lehr.inspectCount.percent = calcPercentStr(lehrOut, lehrIn);
          lehr.rejectCount.percent = calcPercentFromOneStr(lehrOut, lehrIn);
          lehr.rejectCount.count = lehrReject;
          lehr.wareLossPercent = calcPercentStr(lehrReject, shears);
          lehr.efficiencyAgainstShears = calcPercentStr(lehrOut, shears);
          
          // calc aie
          aie.inspectCount.percent = calcPercentFromOneStr(ceReject, lehrOut);
          aie.rejectCount.percent = calcPercentStr(ceReject, lehrOut);
          aie.wareLossPercent = calcPercentStr(ceReject, shears);
        
          // ce delta
          let cdDelta = (lehrOut - ceReject - eol);
          let tmpRejectCount: Count = {
            count: cdDelta,
            percent: calcPercentStr(cdDelta, lehrOut),
            trend: { flat: true },
            rejectPercentDecimals: 2,
            lineCounts: null,
            getPercent: null
          };
          tmpRejectCount.getPercent = () => tmpRejectCount.percent;
          aieOther.hasRejects = true;
          aieOther.rejectCount = tmpRejectCount;
          aieOther.inspectCount = null;
          aieOther.wareLossPercent = calcPercentStr(cdDelta, shears);
        
          // calc ptp
          ptp.inspectCount.percent = calcPercentStr(eol, shears);
          ptp.wareLossPercent = calcPercentFromOneStr(eol, shears);
          ptp.efficiencyAgainstShears = calcPercentStr(eol, shears);

          lineCounts.kpisV1.forEach(kp => kp.recalculate());
        }
    }

   
    function recalculateKpis(lineCounts: LineCounts): void {
      lineCounts.kpis.forEach(kp => kp.recalculate());
      if(lineCounts && lineCounts.isV2){
        reCalcGroupPercents(lineCounts);
      }
    }

    function createShopCounts(
      lineCounts: LineCounts,
      shopCounts: any,
      shopCountsById: any,
      rawCounts: any,
      shopConfig: apsViewModels.ShopConfigurationWithShifts,
      type: apsEnums.WellKnownCheckType,
      dataPointId: string,
      countsInterval: apsDisplayEnums.CountDisplayInterval,
      showSpeed?: boolean): void {

      let dpCount = createCounts(lineCounts, false, dataPointId, rawCounts[dataPointId], countsInterval,
        shopConfig, null, null, null, null, showSpeed);
      shopCounts[type] = dpCount;
      shopCountsById[dataPointId] = dpCount;
      if (showSpeed) {
        lineCounts.gobsProduced = dpCount;
      }
      if (type === apsEnums.WellKnownCheckType.PackedCount) {
        lineCounts.packedCount = dpCount;
      }
    }

    function createLineCounts(
      shopLines: apsViewModels.ShopInspectionLines,
      rawCounts: { [key: string]: apsViewModels.CountItem[] },
      plant: Plant,
      countsInterval: apsDisplayEnums.CountDisplayInterval,
      canDisable: boolean,
      shopConfig: apsViewModels.ShopConfigurationWithShifts,
      isCountsV2?: boolean,
      calcGroupByShears?: boolean): LineCounts {
      let lineCounts: LineCounts = {
        lines: <LineItem[]>shopLines.lines,
        junctions: <LineJunctionItem[]>shopLines.junctions,
        wellKnownMeasures: shopLines.wellKnownMeasures,
        inconsistentEquipmentConfigurations: shopLines.inconsistentEquipmentConfigurations,
        countRejectPercent: plant.countRejectPercent,
        shopConfig: shopConfig,
        equipmentGroups: [],
        equipmentGroupsV1: [],
        equipment: {},
        kpis: [],
        kpisV1: [],
        gobsProduced: null,
        packedCount: null,
        handleInterval: null,
        handleCount: null,
        handleEquipmentHealthUpdate: null,
        updateCounts: null,
        handleIntervalChange: null,
        handleInconsistencyChanged: null,
        kpiCalculations: [],
        heInId: null,
        lehrInId: null,
        packedCountId: null,
        firstInspectionId: null,
        isV2: isCountsV2,
        isCalcGroupByShears: calcGroupByShears,
        usePercentGobs: plant.countRejectPercent === apsEnums.CountRejectPercent.PercentOfGobsProduced
      };

      

          if (!canDisable) {
            lineCounts.lines = lineCounts.lines.filter(x => !x.isDisabled);
          }
          lineCounts.junctions.forEach(junction => {
            junction.inputs = lineCounts.lines.filter(line => line.endJunctionId === junction.id);
            junction.inputs.forEach(line => line.endJunction = junction);

            junction.outputs = lineCounts.lines.filter(line => line.startJunctionId === junction.id);
            junction.outputs.forEach(line => line.startJunction = junction);
          });

          let shopCounts: { [id: number]: Counts } = {};
          let shopCountsById: { [id: string]: Counts } = {};

          let checkTypes = [apsEnums.WellKnownCheckType.GobsProduced,
            apsEnums.WellKnownCheckType.LehrInput, 
            apsEnums.WellKnownCheckType.PackedCount, 
            apsEnums.WellKnownCheckType.LehrOutput, 
            apsEnums.WellKnownCheckType.ColdEndInspectionLoss, 
            apsEnums.WellKnownCheckType.ColdEndTotalLoss
            ];
        

          checkTypes.forEach(ct => {
            let dataPoint = _.find(shopLines.wellKnownMeasures, { type: ct });
            if (!dataPoint) {
              return;
            }

            createShopCounts(lineCounts, shopCounts, shopCountsById, rawCounts, shopConfig,
               ct, dataPoint.dataPointId, countsInterval, ct === apsEnums.WellKnownCheckType.GobsProduced);
               
               if (ct === apsEnums.WellKnownCheckType.GobsProduced) {
                lineCounts.heInId = dataPoint.dataPointId;
              }

               if (ct === apsEnums.WellKnownCheckType.LehrInput) {
                 lineCounts.lehrInId = dataPoint.dataPointId;
               }

              if (ct === apsEnums.WellKnownCheckType.PackedCount) {
                lineCounts.packedCountId = dataPoint.dataPointId;
              }
        
              if (ct === apsEnums.WellKnownCheckType.LehrOutput) {
                lineCounts.firstInspectionId = dataPoint.dataPointId;
              }

          });

   
          

          kpiPairs.forEach(kp => {
            let numerator = shopCounts[kp.numerator];
            let denominator = shopCounts[kp.denominator];
            if (!numerator || !denominator) {
              return;
            }

            let newKpi = {
              numerator: numerator,
              denominator: denominator,
              efficiency: null,
              lossPercent: null,
              losses: null,
              efficiencyName: kp.efficiency,
              efficiencyFormula: null,
              lossesName: kp.losses,
              recalculate: null
            };

            numerator.countsUpdated[kp.efficiency] = true;
            denominator.countsUpdated[kp.efficiency] = true;

            newKpi.recalculate = () => {
              if (!numerator.countsUpdated[newKpi.efficiencyName] || !denominator.countsUpdated[newKpi.efficiencyName]) {
                return;
              }

              numerator.countsUpdated[newKpi.efficiencyName] = false;
              denominator.countsUpdated[newKpi.efficiencyName] = false;

              newKpi.efficiency = calcEfficiency(numerator, denominator);
              newKpi.losses = calcLosses(numerator, denominator);
              newKpi.lossPercent = calcLossPercent(newKpi.losses, denominator);
            };

            newKpi.recalculate();
            lineCounts.kpis.push(newKpi);
          });

          lineCounts.equipment = populateEquipment(
            lineCounts, rawCounts, countsInterval, canDisable,
            shopLines.inconsistentEquipmentConfigurations);
          lineCounts.equipmentGroups = populateEquipmentGroups(lineCounts);

          if(lineCounts.isV2){
            lineCounts.equipmentGroupsV1 = populateEquipmentGroupsV1(lineCounts);

            kpiPairsV1.forEach(kp => {
              let numerator = shopCounts[kp.numerator];
              let denominator = shopCounts[kp.denominator];


              let kpiV1 = {
                numerator: numerator,
                denominator: denominator,
                efficiency: null,
                lossPercent: null,
                losses: null,
                efficiencyName: kp.efficiencyText,
                efficiencyFormula: kp.efficiencyFormula,
                lossesName: kp.lossText,
                lossesFormula: kp.lossFormula,
                recalculate: null,
                name: kp.name,
                order: kp.order
              };

              if(numerator)  
              {
                numerator.countsUpdated[kp.efficiencyText] = true;
                denominator.countsUpdated[kp.efficiencyText] = true;
              }

              kpiV1.recalculate = () => {
                
                let group = _.find(lineCounts.equipmentGroupsV1, x => x.kpiName === kpiV1.name);
                
                if(numerator){
                  numerator.countsUpdated[kpiV1.efficiencyName] = false;
                  denominator.countsUpdated[kpiV1.efficiencyName] = false;
                }
                
                if(group) {  
                  kpiV1.efficiency = (group.efficiencyAgainstShears|| '0.0') + '%'
                  kpiV1.lossPercent = (group.wareLossPercent || '0.0') + '%';
                } 
                
              };            
              lineCounts.kpisV1.push(kpiV1);
            });

          reCalcGroupPercents(lineCounts);
        }

          lineCounts.handleCount = (event: apsViewModels.ShopCountBatchAdded) => {
            let equipmentIds = Object.keys(lineCounts.equipment);
            let updatedEquipmentIds = {};
            let lineEquipmentIds = _.intersection(equipmentIds, _.uniq(_.map(event.counts, 'lineEquipmentId')));
            let result = false;

            let shopCountsToUpdate = event.counts.filter(c => !c.lineEquipmentId);
            shopCountsToUpdate.forEach(sc => {
              if (shopCountsById[sc.dataPointId]) {
                result = shopCountsById[sc.dataPointId].handleCount(event, sc) || result;
              }
            });
            
            lineEquipmentIds.forEach(id => {
              let updated = lineCounts.equipment[id].handleCount(event, event.counts.filter(c => c.lineEquipmentId === id));
              if (updated) {
                updatedEquipmentIds[id] = true;
                result = true;
              }
            });

            if (lineCounts.currentDetailCounts) {
              result = lineCounts.currentDetailCounts.handleCount(event, event.counts) || result;
            }

            if (lineCounts.currentGroup
              && lineCounts.currentGroup.groupUpdated
              && _.some(Object.keys(lineCounts.currentGroup.equipment), key => updatedEquipmentIds[key] === true)) {
              lineCounts.currentGroup.groupUpdated();
            }
            recalculateKpis(lineCounts);

            return result;
          };

          lineCounts.handleInterval = (event: apsViewModels.ShopCountIntervalBatchAdded) => {
            let result = false;
            let equipmentIds = Object.keys(lineCounts.equipment);
            let updatedEquipmentIds = {};

            event.buckets.forEach(bucket => {
              let shopCountsToUpdate = bucket.counts.filter(c => !c.lineEquipmentId);
              shopCountsToUpdate.forEach(sc => {
                if (shopCountsById[sc.dataPointId]) {
                  result = shopCountsById[sc.dataPointId].handleInterval(bucket, sc) || result;
                }
              });

              let lineEquipmentIds = _.intersection(equipmentIds, _.uniq(_.map(bucket.counts, 'lineEquipmentId')));
              lineEquipmentIds.forEach(id => {
                let updated = lineCounts.equipment[id].handleInterval(bucket, bucket.counts.filter(c => c.lineEquipmentId === id));
                if (updated) {
                  updatedEquipmentIds[id] = true;
                  result = true;
                }
              });

              if (lineCounts.currentDetailCounts) {
                result = lineCounts.currentDetailCounts.handleInterval(bucket, bucket.counts) || result;
              }

              if (lineCounts.currentGroup
                && lineCounts.currentGroup.groupUpdated
                && _.some(Object.keys(lineCounts.currentGroup.equipment), key => updatedEquipmentIds[key] === true)) {
                lineCounts.currentGroup.groupUpdated();
              }
            });

            recalculateKpis(lineCounts);

            return result;
          };

          lineCounts.handleEquipmentHealthUpdate = (event: apsViewModels.EquipmentHealthUpdated) => {
            let lineEquipmentItem = lineCounts.equipment[event.lineEquipmentId];
            if(lineEquipmentItem){
              lineEquipmentItem.handleEquipmentHealthUpdate(event);
            }
          };

          lineCounts.updateCounts = (
            counts: { [key: string]: apsViewModels.CountItem[] },
            shopConfig: apsViewModels.ShopConfigurationWithShifts): boolean => {
            let updated = false;
            lineCounts.shopConfig = shopConfig;

            for (let key in shopCounts) {
              let count = shopCounts[key];
              updated = count.updateCounts(counts[count.id], shopConfig) || updated;
            }

            lineCounts.lines.forEach(line => line.equipment.forEach(equipment => {
              updated = equipment.updateCounts(counts, shopConfig) || updated;
            }));

            recalculateKpis(lineCounts);
            return updated;
          };

          lineCounts.handleIntervalChange = (changedInterval: apsDisplayEnums.CountDisplayInterval) => {
            for (let key in shopCounts) {
              let count = shopCounts[key];
              count.countsInterval = changedInterval;
              count.calculateCount();
            }

            for (let equipmentId in lineCounts.equipment) {
              lineCounts.equipment[equipmentId].handleIntervalChange(changedInterval);
            }

            recalculateKpis(lineCounts);
          };

          lineCounts.handleInconsistencyChanged = (event: apsViewModels.InconsistentEquipmentConfigurationChanged) => {
            handleInconsistencyChanged(event, lineCounts.equipment);

          };

          return lineCounts;        
    }    

    return {
      createLineCounts: createLineCounts,
      createLineEquipmentCounts: createLineEquipmentCounts
    };
  })
  .name;


// WEBPACK FOOTER //
// ./src/app/models/counts/lineCounts.ts